import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Wallet,
  ArrowDownToLine,
  ArrowUpFromLine,
  Repeat,
  ListTree,
  Users,
  Building2,
  BarChart3,
  ScrollText,
  Wrench,
  Settings,
  FolderCog,
  Landmark,
  Tag,
  ArrowRightLeft,
  ChevronDown,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { Logo } from '@/components/brand/Logo';

/**
 * RAD Finance — Sidebar
 * --------------------------------------------------------------------------
 * Menu lateral no visual do design system RAD:
 *   - Fundo `--rad-color-sidebar`, borda à direita.
 *   - Logo lockup no topo (clicável → dashboard).
 *   - Item ativo com indicador verde lateral (3px) + fundo levemente verde
 *     (--rad-color-sidebar-active) + texto/ícone na cor da marca.
 *   - Grupos colapsáveis com chevron animado por CSS.
 *
 * Mantém a árvore de navegação e o RBAC por role (ADMIN) do app financeiro.
 */

interface NavLeaf {
  label: string;
  to: string;
  icon: LucideIcon;
  adminOnly?: boolean;
}

interface NavGroup {
  label: string;
  icon: LucideIcon;
  adminOnly?: boolean;
  children: NavLeaf[];
  /** Rota usada para detectar quando o grupo deve ficar expandido por padrão. */
  basePath: string;
}

type NavItem = NavLeaf | NavGroup;

function isGroup(it: NavItem): it is NavGroup {
  return (it as NavGroup).children !== undefined;
}

const NAV: NavItem[] = [
  { label: 'Dashboard', to: '/dashboard', icon: LayoutDashboard },
  { label: 'Caixa', to: '/caixa', icon: Wallet },
  { label: 'Contas a pagar', to: '/contas-pagar', icon: ArrowUpFromLine },
  { label: 'Contas a receber', to: '/contas-receber', icon: ArrowDownToLine },
  { label: 'Recorrências', to: '/recorrentes', icon: Repeat },
  {
    label: 'Cadastros',
    icon: FolderCog,
    basePath: '/cadastros',
    children: [
      { label: 'Contrapartes', to: '/cadastros/contrapartes', icon: Building2 },
      { label: 'Plano de contas', to: '/cadastros/plano-contas', icon: ListTree },
      { label: 'Contas / Caixas', to: '/cadastros/contas', icon: Landmark },
      { label: 'Centros de custo', to: '/cadastros/centros-custo', icon: Tag },
      { label: 'Transferências', to: '/cadastros/transferencias', icon: ArrowRightLeft },
    ],
  },
  { label: 'Relatórios', to: '/relatorios', icon: BarChart3 },
  {
    label: 'Configurações',
    icon: Settings,
    adminOnly: true,
    basePath: '/configuracoes',
    children: [
      { label: 'Usuários', to: '/configuracoes/usuarios', icon: Users, adminOnly: true },
      { label: 'Auditoria', to: '/configuracoes/audit', icon: ScrollText, adminOnly: true },
      { label: 'Manutenção', to: '/configuracoes/manutencao', icon: Wrench, adminOnly: true },
    ],
  },
];

export function Sidebar() {
  const user = useAuthStore((s) => s.user);

  const items = useMemo<NavItem[]>(() => {
    const isAdmin = user?.role === 'ADMIN';
    return NAV.filter((i) => !i.adminOnly || isAdmin).map((i) => {
      if (isGroup(i)) {
        return { ...i, children: i.children.filter((c) => !c.adminOnly || isAdmin) };
      }
      return i;
    });
  }, [user?.role]);

  return (
    <aside className="w-64 shrink-0 h-full flex flex-col bg-sidebar border-r border-line">
      {/* Header — logo lockup */}
      <div className="h-16 flex items-center gap-2 px-4 border-b border-line">
        <NavLink to="/dashboard" className="flex items-center gap-2.5" aria-label="RAD Finance">
          <Logo size={28} />
          <div className="leading-tight">
            <div className="text-sm font-semibold text-fg tracking-wide">
              RAD <span className="text-brand-strong">Finance</span>
            </div>
            <div className="text-[9px] uppercase tracking-[0.18em] text-faint truncate max-w-[150px]">
              {user?.organization?.name ?? 'Grupo RAD'}
            </div>
          </div>
        </NavLink>
      </div>

      {/* Nav body */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {items.map((it) =>
          isGroup(it) ? <NavGroupItem key={it.label} group={it} /> : <LeafLink key={it.to} item={it} />,
        )}
      </nav>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-line text-[11px] text-faint flex items-center justify-between">
        <span>Plataforma RAD</span>
        <span className="font-mono">v0.1</span>
      </div>
    </aside>
  );
}

function LeafLink({ item, nested = false }: { item: NavLeaf; nested?: boolean }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-2.5 rounded-md py-2 text-sm transition-colors duration-fast ease-rad',
          nested ? 'pl-9 pr-3' : 'pl-3 pr-3',
          isActive ? 'text-brand-strong font-semibold' : 'text-subtle hover:text-fg',
          'hover:bg-surface-2',
        )
      }
      style={({ isActive }) =>
        isActive ? { backgroundColor: 'var(--rad-color-sidebar-active)' } : undefined
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-brand-500" aria-hidden />
          )}
          <Icon className={cn('w-4 h-4 shrink-0', isActive && 'text-brand-strong')} aria-hidden />
          <span className="truncate flex-1">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

function NavGroupItem({ group }: { group: NavGroup }) {
  const location = useLocation();
  const isInside = location.pathname.startsWith(group.basePath);
  const [open, setOpen] = useState(isInside);
  const Icon = group.icon;

  // Mantém o grupo aberto ao navegar para uma rota interna.
  useEffect(() => {
    if (isInside) setOpen(true);
  }, [isInside]);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          'w-full flex items-center gap-2.5 rounded-md py-2 pl-3 pr-3 text-sm font-medium transition-colors duration-fast ease-rad',
          isInside ? 'text-fg' : 'text-subtle hover:text-fg',
          'hover:bg-surface-2',
        )}
      >
        <Icon className={cn('w-4 h-4 shrink-0', isInside && 'text-brand-strong')} aria-hidden />
        <span className="flex-1 text-left truncate">{group.label}</span>
        <ChevronDown
          className={cn(
            'w-3.5 h-3.5 text-faint transition-transform duration-fast ease-rad',
            open ? 'rotate-0' : '-rotate-90',
          )}
          aria-hidden
        />
      </button>

      {open && (
        <div className="mt-1 space-y-0.5">
          {group.children.map((child) => (
            <LeafLink key={child.to} item={child} nested />
          ))}
        </div>
      )}
    </div>
  );
}
